import React from "react";
import { Link } from "wouter";
import { Header } from "src/components/Header";
import { Page } from "src/components/Page";
import { Goal } from "src/database/types";
import styled from "styled-components";
import { GoalForm } from "../GoalForm";
import { ChevronDown, ChevronUp, Pencil, PlusIcon } from "lucide-react";
import { WeekReport } from "src/database/models/GoalSummary";
import { GoalSummary } from "./GoalSummary";
import { useLiveQuery } from "dexie-react-hooks";
import { getGoals } from "src/database/queries/goals";

export function Goals() {
  const [form, setForm] = React.useState<{ data?: Goal } | null>(null);
  const goals = useLiveQuery(() => getGoals());
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set());

  const createGoal = () => {
    setForm({});
  };

  const toggleExpand = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setExpanded((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const closeForm = () => setForm(null);

  const onSave = () => {
    closeForm();
  };

  const openForm = (e: React.MouseEvent, goal: Goal) => {
    e.stopPropagation();
    setForm({ data: goal });
  };

  return (
    <Page maxWidth={600}>
      <Header title="Goals">
        <Header.Button icon={PlusIcon} onClick={() => createGoal()}>
          New Goal
        </Header.Button>
      </Header>
      <GoalList>
        <tbody>
          {goals?.map((goal) => {
            const isExpanded = expanded.has(goal.id);
            const ratio = getRatio(goal.summary);
            return (
              <React.Fragment key={goal.id}>
                <GoalRow
                  data-testid="goal-row"
                  $expanded={isExpanded}
                  $last={!isExpanded}
                  onClick={(e) => toggleExpand(e, goal.id)}
                >
                  <GoalNameCell>
                    <GoalNameLink
                      href={`/goals/${goal.id}`}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`goal-link-${goal.id}`}
                    >
                      <GoalNameText>{goal.name}</GoalNameText>
                    </GoalNameLink>
                  </GoalNameCell>
                  <td>
                    {goal.habits.length > 0 ? (
                      <GoalSummary summary={goal.summary} weekSummaries={goal.weekSummaries} />
                    ) : (
                      <Slate>Add habits to track this goal's progress</Slate>
                    )}
                  </td>
                  <TrendCell>
                    {goal.habits.length > 0 ? (
                      <WeekTrack>
                        {goal.weekSummaries.map((week, idx) => (
                          <WeekHealth $ratio={getRatio(week)} key={idx} />
                        ))}
                        <WeekHealth $ratio={ratio} />
                      </WeekTrack>
                    ) : null}
                  </TrendCell>
                  <ActionCell>
                    <Actions>
                      <Pencil onClick={(e) => openForm(e, goal)} size={14} />
                      {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </Actions>
                  </ActionCell>
                </GoalRow>

                {isExpanded && (
                  <>
                    <GoalHabit>
                      <th>Habit</th>
                      <th>This Week</th>
                      <th style={{ width: "66px" }}>Trend</th>
                      <th style={{ width: "56px" }} aria-hidden />
                    </GoalHabit>
                    {goal.habits.map((habit, idx) => (
                      <GoalHabit
                        key={habit.id}
                        data-testid="goal-habit-row"
                        $last={idx === goal.habits.length - 1}
                      >
                        <td>{habit.name}</td>
                        <td>
                          <GoalSummary
                            summary={habit.summary}
                            weekSummaries={habit.weekSummaries}
                          />
                        </td>
                        <td style={{ width: "66px" }}>
                          <WeekTrack>
                            {habit.weekSummaries.map((week, widx) => (
                              <WeekHealth $ratio={getRatio(week)} key={widx} />
                            ))}
                            <WeekHealth $ratio={getRatio(habit.summary)} />
                          </WeekTrack>
                        </td>
                        <td style={{ width: "56px" }} />
                      </GoalHabit>
                    ))}
                  </>
                )}
                <Gap>
                  <td colSpan={4}></td>
                </Gap>
              </React.Fragment>
            );
          })}
        </tbody>
      </GoalList>
      {form && <GoalForm goal={form.data} onSave={onSave} onCancel={closeForm} />}
    </Page>
  );
}

function getRatio({ completed, total }: WeekReport) {
  return total === 0 ? 1 : completed / total;
}

const GoalList = styled.table`
  width: 100%;
  table-layout: fixed;
`;

const Slate = styled.div`
  color: var(--text-secondary-color);
`;

const WeekTrack = styled.div`
  display: flex;
  gap: 1px;
  padding: 1px;
  align-items: flex-end;
  height: 24px;
  width: 66px;
  justify-content: flex-end;
  margin-left: auto;
  background: var(--box-color-darken);
`;

const WeekHealth = styled.span<{ $ratio: number }>`
  width: 12px;
  height: ${({ $ratio }) => Math.max(5, 100 * $ratio)}%;
  background-color: var(--theme-color-light);
`;

const Row = styled.tr<{ $last?: boolean }>`
  color: var(--text-primary-color);
  border-bottom: none;
  td,
  th {
    letter-spacing: 0.01em;
    padding: 8px 10px;
    font-size: 14px;
    background: var(--box-color);
  }
  ${({ $last }) =>
    $last &&
    `
      td:first-child {
        border-radius: 0 0 0 6px;
      }
      td:last-child {
        border-radius: 0 0 6px 0;
      }
    `}
`;

const GoalRow = styled(Row)<{ $expanded: boolean }>`
  cursor: pointer;
  ${({ $expanded }) => $expanded && "border-bottom: none;"}
  td {
    vertical-align: middle;
  }
  td:first-child {
    border-radius: ${({ $expanded }) => ($expanded ? "6px 0 0 0" : "6px 0 0 6px")};
  }
  td:last-child {
    border-radius: ${({ $expanded }) => ($expanded ? "0 6px 0 0" : "0 6px 6px 0")};
  }
`;

const GoalNameCell = styled.td`
  width: 28%;
  min-width: 0;
  white-space: nowrap;
`;

const GoalNameLink = styled(Link)`
  display: block;
  color: inherit;
  text-decoration: none;
  min-width: 0;
  &:hover .goal-name-text {
    text-decoration: underline;
  }
`;

const GoalNameText = styled.span.attrs({ className: "goal-name-text" })`
  display: block;
  font-weight: 300;
  font-size: 16px;
  letter-spacing: 0.01em;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TrendCell = styled.td`
  width: 66px;
  box-sizing: content-box;
  white-space: nowrap;
`;

const ActionCell = styled.td`
  width: 56px;
  box-sizing: border-box;
`;

const Actions = styled.div`
  height: 100%;
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: flex-end;
  svg {
    display: block;
  }
`;

const GoalHabit = styled(Row)`
  font-size: 14px;
  border-top: 1px solid var(--bg-color);
  th {
    font-weight: 400;
    white-space: nowrap;
  }
  td {
    font-weight: 200;
    padding: 10px 10px;
  }
`;

const Gap = styled.tr`
  display: table-row;
  td {
    height: 5px;
  }
`;
